import { useState } from 'react';

export default function PatientIntakeForm() {
  const [formData, setFormData] = useState({
    patientName: '',
    dateOfBirth: '',
    patientAge: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    serviceType: '',
    preferredStartDate: '',
    specialRequirements: ''
  });

  const [preexistingConditions, setPreexistingConditions] = useState<string[]>([]);
  const [additionalServices, setAdditionalServices] = useState<string[]>([]);

  const conditionsList = [
    'Diabetes',
    'Hipertensión',
    'Enfermedades cardíacas',
    'Asma / EPOC',
    'Artritis',
    'Cáncer',
    'Enfermedad renal',
    'Alzheimer / Demencia',
    'Parkinson',
    'Accidente cerebrovascular (ACV)',
    'Epilepsia',
    'VIH / SIDA',
    'Hepatitis',
    'Obesidad',
    'Depresión / Ansiedad',
    'Otra condición'
  ];

  const additionalServicesList = [
    { name: 'Fisioterapia', icon: 'ri-walk-line' },
    { name: 'Terapia del Lenguaje', icon: 'ri-speak-line' },
    { name: 'Terapia Cognitiva', icon: 'ri-brain-line' },
    { name: 'Laboratorio a Domicilio', icon: 'ri-test-tube-line' },
    { name: 'Terapia Respiratoria', icon: 'ri-lungs-line' },
    { name: 'Nutrición', icon: 'ri-restaurant-line' },
    { name: 'Tratamiento Ambulatorio', icon: 'ri-syringe-line' },
    { name: 'Hospitalización en Casa', icon: 'ri-hotel-bed-line' }
  ];

  const equipmentRentalList = [
    { name: 'Concentrador de Oxígeno', icon: 'ri-bubble-chart-line' },
    { name: 'Paral de Suero', icon: 'ri-drop-line' },
    { name: 'Nebulizador', icon: 'ri-windy-line' },
    { name: 'Equipo de Aspiración', icon: 'ri-first-aid-kit-line' },
    { name: 'Cama Clínica', icon: 'ri-hotel-bed-line' }
  ];

  const [equipmentRental, setEquipmentRental] = useState<string[]>([]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleConditionChange = (condition: string) => {
    setPreexistingConditions(prev => 
      prev.includes(condition) 
        ? prev.filter(c => c !== condition)
        : [...prev, condition]
    );
  };

  const handleAdditionalServiceChange = (service: string) => {
    setAdditionalServices(prev => 
      prev.includes(service) 
        ? prev.filter(s => s !== service)
        : [...prev, service]
    );
  };

  const handleEquipmentRentalChange = (equipment: string) => {
    setEquipmentRental(prev => 
      prev.includes(equipment) 
        ? prev.filter(e => e !== equipment)
        : [...prev, equipment]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        formBody.append(key, value);
      });
      formBody.append('preexistingConditions', preexistingConditions.join(', '));
      formBody.append('additionalServices', additionalServices.join(', '));
      formBody.append('equipmentRental', equipmentRental.join(', '));

      const response = await fetch('https://readdy.ai/api/form/d6smg8o37h4e5cfhpqh0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          patientName: '',
          dateOfBirth: '',
          patientAge: '',
          email: '',
          phone: '',
          address: '',
          city: '',
          state: '',
          zipCode: '',
          serviceType: '',
          preferredStartDate: '',
          specialRequirements: ''
        });
        setPreexistingConditions([]);
        setAdditionalServices([]);
        setEquipmentRental([]);
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="intake" className="py-20 bg-gradient-to-br from-uri-green/5 to-uri-teal/5">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-5 gap-12 items-start">
          {/* Left Side - Information */}
          <div className="lg:col-span-2">
            <h3 className="text-4xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>
              Comience su Viaje de Cuidado
            </h3>
            <p className="text-lg text-gray-600 mb-8 leading-relaxed">
              Complete nuestro formulario de admisión de pacientes para comenzar a recibir asistencia profesional de salud. Su información es segura y confidencial.
            </p>

            {/* Trust Badges */}
            <div className="space-y-4 mb-8">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-uri-teal/10 rounded-lg flex items-center justify-center">
                  <i className="ri-shield-check-line text-uri-teal text-xl"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 text-sm">Cumple con HIPAA</h5>
                  <p className="text-xs text-gray-600">Sus datos están protegidos</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-uri-green/10 rounded-lg flex items-center justify-center">
                  <i className="ri-lock-line text-uri-green text-xl"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 text-sm">Encriptado y Seguro</h5>
                  <p className="text-xs text-gray-600">Encriptación SSL habilitada</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-uri-blue/10 rounded-lg flex items-center justify-center">
                  <i className="ri-time-line text-uri-blue text-xl"></i>
                </div>
                <div>
                  <h5 className="font-semibold text-gray-900 text-sm">Respuesta Rápida</h5>
                  <p className="text-xs text-gray-600">Respondemos dentro de 24 horas</p>
                </div>
              </div>
            </div>

            {/* Illustration */}
            <div className="hidden lg:block">
              <img 
                src="https://public.readdy.ai/ai/img_res/c63cb3efc70f25ef94204f1f45bdc877.jpg"
                alt="Ilustración de formulario de admisión de pacientes"
                className="w-full rounded-2xl"
              />
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="lg:col-span-3">
            <form id="patient-intake-form" data-readdy-form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-xl p-8">
              {/* Progress Indicator */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-uri-teal">Paso 1 de 1</span>
                  <span className="text-xs text-gray-500">Información del Paciente</span>
                </div>
                <div className="w-full h-2 bg-gray-200 rounded-full">
                  <div className="w-full h-full bg-gradient-to-r from-uri-green to-uri-teal rounded-full"></div>
                </div>
              </div>

              <h4 className="text-2xl font-bold text-gray-900 mb-6">Información del Paciente</h4>

              {/* Patient Name */}
              <div className="mb-5">
                <label htmlFor="patientName" className="block text-sm font-semibold text-gray-700 mb-2">
                  Nombre Completo del Paciente *
                </label>
                <input
                  type="text"
                  id="patientName"
                  name="patientName"
                  value={formData.patientName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                  placeholder="Ingrese nombre completo"
                />
              </div>

              {/* Date of Birth and Age */}
              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="dateOfBirth" className="block text-sm font-semibold text-gray-700 mb-2">
                    Fecha de Nacimiento *
                  </label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="patientAge" className="block text-sm font-semibold text-gray-700 mb-2">
                    Edad del Paciente *
                  </label>
                  <input
                    type="number"
                    id="patientAge"
                    name="patientAge"
                    value={formData.patientAge}
                    onChange={handleChange}
                    required
                    min="0"
                    max="120"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                    placeholder="Edad en años"
                  />
                </div>
              </div>

              {/* Contact Information */}
              <div className="grid md:grid-cols-2 gap-5 mb-5">
                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    Correo Electrónico *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                    placeholder="correo@ejemplo.com"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-2">
                    Número de Teléfono *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                    placeholder="(123) 456-7890"
                  />
                </div>
              </div>

              {/* Address */}
              <div className="mb-5">
                <label htmlFor="address" className="block text-sm font-semibold text-gray-700 mb-2">
                  Dirección *
                </label>
                <input
                  type="text"
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                  placeholder="Calle Principal 123"
                />
              </div>

              <div className="grid md:grid-cols-3 gap-5 mb-5">
                <div>
                  <label htmlFor="city" className="block text-sm font-semibold text-gray-700 mb-2">
                    Ciudad *
                  </label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                    placeholder="Ciudad"
                  />
                </div>
                <div>
                  <label htmlFor="state" className="block text-sm font-semibold text-gray-700 mb-2">
                    Estado *
                  </label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                    placeholder="Estado"
                  />
                </div>
                <div>
                  <label htmlFor="zipCode" className="block text-sm font-semibold text-gray-700 mb-2">
                    Código Postal *
                  </label>
                  <input
                    type="text"
                    id="zipCode"
                    name="zipCode"
                    value={formData.zipCode}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                    placeholder="12345"
                  />
                </div>
              </div>

              {/* Preexisting Conditions */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  Enfermedades Preexistentes
                </label>
                <p className="text-xs text-gray-500 mb-3">Seleccione todas las condiciones que apliquen al paciente</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 bg-gray-50 rounded-lg max-h-64 overflow-y-auto">
                  {conditionsList.map((condition) => (
                    <label 
                      key={condition} 
                      className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded-md transition-colors"
                    >
                      <input
                        type="checkbox"
                        name="preexistingConditions"
                        value={condition}
                        checked={preexistingConditions.includes(condition)}
                        onChange={() => handleConditionChange(condition)}
                        className="w-4 h-4 text-uri-teal accent-uri-teal rounded"
                      />
                      <span className="text-sm text-gray-700">{condition}</span>
                    </label>
                  ))}
                </div>
                {preexistingConditions.length > 0 && (
                  <p className="text-xs text-uri-teal mt-2">
                    {preexistingConditions.length} condición(es) seleccionada(s)
                  </p>
                )}
              </div>

              {/* Additional Services */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <i className="ri-service-line mr-2 text-uri-teal"></i>
                  Servicios Especializados
                </label>
                <p className="text-xs text-gray-500 mb-3">Seleccione las terapias y servicios adicionales que requiera</p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-4 bg-gray-50 rounded-lg">
                  {additionalServicesList.map((service) => (
                    <label 
                      key={service.name} 
                      className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded-md transition-colors"
                    >
                      <input
                        type="checkbox"
                        name="additionalServices"
                        value={service.name}
                        checked={additionalServices.includes(service.name)}
                        onChange={() => handleAdditionalServiceChange(service.name)}
                        className="w-4 h-4 text-uri-teal accent-uri-teal rounded"
                      />
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${service.icon} text-uri-teal text-sm`}></i>
                      </div>
                      <span className="text-xs text-gray-700">{service.name}</span>
                    </label>
                  ))}
                </div>
                {additionalServices.length > 0 && (
                  <p className="text-xs text-uri-teal mt-2">
                    {additionalServices.length} servicio(s) seleccionado(s)
                  </p>
                )}
              </div>

              {/* Equipment Rental */}
              <div className="mb-5">
                <label className="block text-sm font-semibold text-gray-700 mb-3">
                  <i className="ri-tools-line mr-2 text-uri-green"></i>
                  Alquiler de Equipos Especializados
                </label>
                <p className="text-xs text-gray-500 mb-3">Seleccione los equipos médicos que necesita alquilar</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 bg-gray-50 rounded-lg">
                  {equipmentRentalList.map((equipment) => (
                    <label 
                      key={equipment.name} 
                      className="flex items-center gap-2 cursor-pointer hover:bg-white p-2 rounded-md transition-colors"
                    >
                      <input
                        type="checkbox"
                        name="equipmentRental"
                        value={equipment.name}
                        checked={equipmentRental.includes(equipment.name)}
                        onChange={() => handleEquipmentRentalChange(equipment.name)}
                        className="w-4 h-4 text-uri-green accent-uri-green rounded"
                      />
                      <div className="w-5 h-5 flex items-center justify-center">
                        <i className={`${equipment.icon} text-uri-green text-sm`}></i>
                      </div>
                      <span className="text-xs text-gray-700">{equipment.name}</span>
                    </label>
                  ))}
                </div>
                {equipmentRental.length > 0 && (
                  <p className="text-xs text-uri-green mt-2">
                    {equipmentRental.length} equipo(s) seleccionado(s)
                  </p>
                )}
              </div>

              {/* Service Type */}
              <div className="mb-5">
                <label htmlFor="serviceType" className="block text-sm font-semibold text-gray-700 mb-2">
                  Tipo de Servicio Necesario *
                </label>
                <select
                  id="serviceType"
                  name="serviceType"
                  value={formData.serviceType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                >
                  <option value="">Seleccione tipo de servicio</option>
                  <option value="residential-care">Servicios de Cuidado Residencial</option>
                  <option value="hospital-public">Asistencia Hospitalaria - Instalación Pública</option>
                  <option value="hospital-private">Asistencia Hospitalaria - Centro Privado</option>
                </select>
              </div>

              {/* Preferred Start Date */}
              <div className="mb-5">
                <label htmlFor="preferredStartDate" className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha de Inicio Preferida *
                </label>
                <input
                  type="date"
                  id="preferredStartDate"
                  name="preferredStartDate"
                  value={formData.preferredStartDate}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm"
                />
              </div>

              {/* Special Requirements */}
              <div className="mb-6">
                <label htmlFor="specialRequirements" className="block text-sm font-semibold text-gray-700 mb-2">
                  Requisitos Especiales o Notas
                </label>
                <textarea
                  id="specialRequirements"
                  name="specialRequirements"
                  value={formData.specialRequirements}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-uri-teal focus:border-transparent text-sm resize-none"
                  placeholder="Por favor describa cualquier requisito especial o condición médica que debamos conocer (máx 500 caracteres)"
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">{formData.specialRequirements.length}/500 caracteres</p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-4 bg-gradient-to-r from-uri-green to-uri-teal text-white text-base font-semibold rounded-lg hover:from-uri-green-dark hover:to-uri-teal-dark transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar Solicitud'}
              </button>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="mt-4 p-4 bg-uri-green/10 border border-uri-green/20 rounded-lg">
                  <div className="flex items-start gap-2">
                    <i className="ri-check-circle-line text-uri-green text-xl flex-shrink-0 mt-0.5"></i>
                    <div>
                      <p className="text-sm font-semibold text-uri-green-dark mb-1">¡Solicitud enviada exitosamente!</p>
                      <p className="text-xs text-gray-700 leading-relaxed">
                        Su reserva será confirmada tan pronto un agente lo(a) contacte a través de este correo electrónico o los números de teléfono registrados en su solicitud.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 flex items-center gap-2">
                    <i className="ri-error-warning-line text-lg"></i>
                    Hubo un error al enviar su solicitud. Por favor intente de nuevo o contáctenos directamente.
                  </p>
                </div>
              )}

              <p className="text-xs text-gray-500 text-center mt-4">
                Para asuntos urgentes, por favor contáctenos directamente.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
